<script setup>
  import {nextTick, ref} from "vue";

  import ComponentAlert from "@components/Alerts/Alert.vue";
  import ComponentDialog from "@components/Dialogs/Dialog.vue";
  import {useForm} from "@inertiajs/vue3";
  import ComponentRecordStatus from "@pages/Records/Components/RecordStatus.vue";
  import {MedCheckbox, MedDescription, MedFormField, MedLabel} from "@pharma4u/medicheck-components";
  import {debounce} from "es-toolkit";

  defineOptions({
    name: "ComponentRecordEasyComDialog",
  });

  const refDialog = ref(null);
  const currentRecord = ref(null);

  const form = useForm({
    doctorinfocreated: false,
    doctordocumentcreated: false,
    patientinfocreated: false,
    patientdocumentcreated: false,
    documentationcreated: false,
  });

  const save = debounce(function () {
    nextTick(() => {
      form.patch(route("records.update", {patient: currentRecord.value.patient_id, record: currentRecord.value.id}), {
        headers: {
          // avoid updating the edited_at values when just changing the status
          "Mc-Silent-Update": true,
        },
        preserveScroll: true,
      });
    });
  }, 800);

  function open(record) {
    currentRecord.value = record;

    form.doctorinfocreated = record.doctorinfocreated !== null;
    form.doctordocumentcreated = record.doctordocumentcreated !== null;
    form.patientinfocreated = record.patientinfocreated !== null;
    form.patientdocumentcreated = record.patientdocumentcreated !== null;
    form.documentationcreated = record.documentationcreated !== null;

    refDialog.value.open();
  }

  function close() {
    refDialog.value.close();
  }

  defineExpose({
    open,
    close,
  });
</script>

<template>
  <ComponentDialog
    ref="refDialog"
    dialog-title="Easy-com Kommunikation"
    dialog-width="medium"
    primary-label="Speichern"
    data-test="easyCom-dialog"
    @primary-button-clicked="save()"
  >
    <template #content>
      <ul class="space-y-6">
        <li class="space-y-2">
          <h3 class="font-bold">Arztkommunikation</h3>
          <MedFormField orientation="horizontal" :disabled="form.processing">
            <MedCheckbox
              v-model="form.doctorinfocreated"
              variant="accent"
              :default-value="true"
              data-test="easyCom-dialog-doctorinfocreated-checkbox"
            />

            <MedLabel>E-Mail gesendet</MedLabel>
          </MedFormField>

          <MedFormField orientation="horizontal" :disabled="form.processing">
            <MedCheckbox
              v-model="form.doctordocumentcreated"
              variant="accent"
              :default-value="true"
              data-test="easyCom-dialog-doctordocumentcreated-checkbox"
            />

            <MedLabel>PDF gespeichert / gedruckt</MedLabel>
          </MedFormField>
        </li>

        <li class="space-y-2">
          <h3 class="font-bold">Patientenkommunikation</h3>

          <MedFormField orientation="horizontal" :disabled="form.processing">
            <MedCheckbox
              v-model="form.patientinfocreated"
              variant="accent"
              :default-value="true"
              data-test="easyCom-dialog-patientinfocreated-checkbox"
            />

            <MedLabel>E-Mail gesendet</MedLabel>
          </MedFormField>

          <MedFormField orientation="horizontal" :disabled="form.processing">
            <MedCheckbox
              v-model="form.patientdocumentcreated"
              variant="accent"
              :default-value="true"
              data-test="easyCom-dialog-patientdocumentcreated-checkbox"
            />

            <MedLabel>PDF gespeichert / gedruckt</MedLabel>
          </MedFormField>
        </li>

        <li v-if="form.documentationcreated" class="space-y-2">
          <h3 class="font-bold text-gray-500">Dokumentation / PCNE</h3>
          <component-alert type="info" class="text-xs">
            Die Dokumentation nach PCNE wurde im MediCheck eingestellt. Vorhandene Dokumentation aus früheren Analysen
            wird Ihnen noch angezeigt, kann aber nicht mehr bearbeitet werden.
          </component-alert>
          <div>
            <MedFormField orientation="horizontal" :disabled="form.processing">
              <MedCheckbox
                v-model="form.documentationcreated"
                disabled
                variant="accent"
                :default-value="true"
                data-test="easyCom-dialog-documentationcreated-checkbox"
              />

              <MedLabel>Analyse im MediCheck dokumentiert</MedLabel>

              <MedDescription>(problembezogen in den Analyseergebnissen unter „Dokumentation / PCNE“)</MedDescription>
            </MedFormField>
          </div>
        </li>
      </ul>

      <div class="mt-4 border-t-1 pt-4">
        <h3 class="font-bold">Status der Variante</h3>
        <div v-if="currentRecord?.closed" class="mt-1 text-sm text-gray-600">
          Diese Variante wurde von Ihnen am
          <span class="font-semibold text-gray-900">{{ currentRecord?.closed }}</span>
          auf „abgeschlossen“ gesetzt.
        </div>

        <ComponentRecordStatus :record="currentRecord" class="mt-1" inner-class="bottom-8 min-w-24 max-w-32" />
      </div>
    </template>
  </ComponentDialog>
</template>
